/**
 * Cerberus identity platform — frontend config.
 *
 * All values are env-driven (Vite env vars prefixed `VITE_`) with prod
 * defaults baked in so the app works out of the box on the deployed bundle.
 */

function readEnv(key: string): string | undefined {
  const value = (import.meta.env as Record<string, string | undefined>)[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

const DEFAULT_API_URL =
  'https://ik8729j7e1.execute-api.us-east-1.amazonaws.com/'
const DEFAULT_DEV_API_URL = '/api/cerberus'
const DEFAULT_AUTH_UI_URL = 'https://id.francisquini.ai'
const DEFAULT_ISSUER = 'https://id.francisquini.ai'
const DEFAULT_ACCEPTED_DOMAINS: readonly string[] = ['finance']

/** Cerberus API base URL. Production HttpApi default. */
function getCerberusApiUrl(): string {
  return (
    readEnv('VITE_CERBERUS_API_URL') ??
    readEnv('VITE_CERBERUS_API') ??
    (import.meta.env.DEV ? DEFAULT_DEV_API_URL : undefined) ??
    DEFAULT_API_URL
  )
}

/**
 * Cerberus Auth UI host. Cerberus é proprietário (Etapa 07 do runbook):
 * SPA própria em id.francisquini.ai com endpoints REST (signup/login/MFA/
 * reset). Não usa Cognito Hosted UI — Cognito existe debaixo do capô como
 * password storage (USER_SRP via cognito-idp:InitiateAuth), mas o frontend
 * sempre fala com a API REST do Cerberus, nunca com /oauth2/* diretamente.
 */
function getCerberusAuthUiUrl(): string {
  return readEnv('VITE_CERBERUS_AUTH_UI_URL') ?? DEFAULT_AUTH_UI_URL
}

/**
 * JWT `iss` claim must match this value. Cerberus stack emits
 * `https://auth.francisquini.ai` regardless of the API gateway URL.
 */
export function getCerberusIssuer(): string {
  return readEnv('VITE_CERBERUS_ISSUER') ?? DEFAULT_ISSUER
}

/** Domains ariadne knows how to render. */
function getCerberusAcceptedDomains(): readonly string[] {
  const raw = readEnv('VITE_CERBERUS_ACCEPTED_DOMAINS')
  if (!raw) return DEFAULT_ACCEPTED_DOMAINS
  const parts = raw
    .split(',')
    .map((d) => d.trim())
    .filter((d) => d.length > 0)
  return parts.length > 0 ? parts : DEFAULT_ACCEPTED_DOMAINS
}

/**
 * Eagerly-evaluated constants — fine for production where env is fixed at
 * bundle time. Tests should prefer the `get*` accessors above which re-read
 * env on every call (compatible with vi.stubEnv).
 */
export const CERBERUS_API_URL: string = getCerberusApiUrl()
export const CERBERUS_AUTH_UI_URL: string = getCerberusAuthUiUrl()
export const CERBERUS_ACCEPTED_DOMAINS: readonly string[] =
  getCerberusAcceptedDomains()

/** Auto-refresh access token if it expires within this many seconds. */
export const REFRESH_LEAD_SECONDS = 60

/** JWKS cache TTL (ms). */
export const JWKS_CACHE_TTL_MS = 60 * 60 * 1000

/**
 * Returns the JWKS endpoint URL from the cerberus API.
 */
export function jwksUrl(apiUrl: string = CERBERUS_API_URL): string {
  const base = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`
  return `${base}.well-known/jwks.json`
}
